import axios from "axios";
import { Logout } from "../store/actions/AuthActions";
import {
  logVersionChange,
  getVersionOverride,
  isValidVersion,
} from "../utils/versionUtils";

const VERSION_STORAGE_KEY = "app_version";

/**
 * Fetches the current version from the server
 * Supports development override for testing
 */
export async function fetchCurrentVersion() {
  try {
    // Check for development override first
    const override = getVersionOverride();
    if (override && process.env.NODE_ENV === "development") {
      console.log(`🔧 Using development version override: ${override}`);
      return override;
    }

    const response = await axios.get(`${process.env.PUBLIC_URL}/version.json`);
    const version = response.data.version;

    if (!isValidVersion(version)) {
      console.error("Invalid version format received from server:", version);
      return null;
    }

    return version;
  } catch (error) {
    console.error("Error fetching version:", error);
    return null;
  }
}

/**
 * Gets the stored version from localStorage
 */
export function getStoredVersion() {
  return localStorage.getItem(VERSION_STORAGE_KEY);
}

/**
 * Stores the version in localStorage
 */
export function storeVersion(version) {
  localStorage.setItem(VERSION_STORAGE_KEY, version);
}

/**
 * Compares stored version with current version and performs logout if different
 */
export async function checkVersionAndLogout(dispatch, navigate) {
  try {
    const currentVersion = await fetchCurrentVersion();

    if (!currentVersion) {
      console.warn("Could not fetch current version, skipping version check");
      return;
    }

    const storedVersion = getStoredVersion();

    // If no stored version, this is the first time, just store it
    if (!storedVersion) {
      console.log(
        `🎯 First time loading app, storing version: ${currentVersion}`,
      );
      storeVersion(currentVersion);
      logVersionChange(null, currentVersion, "initial_load");
      return;
    }

    // If versions differ, perform logout
    if (storedVersion !== currentVersion) {
      console.log(
        `🔄 Version changed from ${storedVersion} to ${currentVersion}. Performing logout...`,
      );

      // Log the version change
      logVersionChange(storedVersion, currentVersion, "logout");

      // Clear all stored data including the version
      clearVersionData();

      // Dispatch logout action
      dispatch(Logout(navigate));

      // Store the new version after logout
      storeVersion(currentVersion);

      console.log(
        `✅ Logout completed and new version stored: ${currentVersion}`,
      );
    } else {
      console.log(`✅ Version check passed: ${currentVersion}`);
    }
  } catch (error) {
    console.error("❌ Error in version check:", error);
  }
}

/**
 * Clears version-related data from localStorage
 */
export function clearVersionData() {
  localStorage.removeItem(VERSION_STORAGE_KEY);
}

/**
 * Forces a version check and logout regardless of version difference
 * Useful for testing or forced updates
 */
export async function forceVersionLogout(dispatch, navigate) {
  try {
    const currentVersion = await fetchCurrentVersion();
    const storedVersion = getStoredVersion();

    if (currentVersion) {
      console.log("🚀 Forcing logout due to version update...");

      // Log the forced version change
      logVersionChange(storedVersion, currentVersion, "forced_logout");

      // Clear all stored data
      clearVersionData();

      // Dispatch logout action
      dispatch(Logout(navigate));

      // Store the new version after logout
      storeVersion(currentVersion);

      console.log(`✅ Forced logout completed: ${currentVersion}`);
    }
  } catch (error) {
    console.error("❌ Error in forced version logout:", error);
  }
}

/**
 * Gets version status information for debugging
 */
export function getVersionStatus() {
  return {
    stored: getStoredVersion(),
    override: getVersionOverride(),
    timestamp: new Date().toISOString(),
    environment: process.env.NODE_ENV,
  };
}

/**
 * Validates version format before storing
 */
export function validateAndStoreVersion(version) {
  if (!isValidVersion(version)) {
    console.error("Invalid version format, not storing:", version);
    return false;
  }

  storeVersion(version);
  return true;
}
